import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Loader2, Search, Building2, User, AlertTriangle, CheckCircle, FileBarChart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatExact = (v) => {
  if (v === null || v === undefined || isNaN(v)) return '0';
  return Math.round(v).toLocaleString('fr-FR');
};

/**
 * Modal allowing the admin to search and pick a client, then continue to the statement dialog.
 * On selection, calls onClientPicked(client) which the parent uses to open ClientStatementDialog.
 */
const StatementGeneratorModal = ({ open, onOpenChange, onClientPicked }) => {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  // Reset state when reopened
  useEffect(() => {
    if (open) {
      setQuery('');
      setItems([]);
      setError(null);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Debounced search (initial load shows top debtors when query empty)
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/admin/clients/searchable-debtors`, {
          params: { q: query, limit: 20 }
        });
        setItems(res.data?.items || []);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Erreur lors de la recherche');
      } finally {
        setLoading(false);
      }
    }, query ? 250 : 0);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [open, query]);

  const handlePick = (item) => {
    onClientPicked({
      id: item.id,
      companyName: item.isCompany ? item.displayName : null,
      firstName: item.isCompany ? null : item.displayName.split(' ')[0],
      lastName: item.isCompany ? null : item.displayName.split(' ').slice(1).join(' '),
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="statement-generator-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-700">
            <FileBarChart className="w-5 h-5" />
            Générer un relevé de compte
          </DialogTitle>
          <DialogDescription className="text-xs">
            Sélectionnez un client. Les débiteurs avec un solde à régler apparaissent en premier.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            ref={inputRef}
            placeholder="Rechercher un client (nom, raison sociale, email)..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 h-10 border-gray-300"
            data-testid="statement-client-search"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700">
            <AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> {error}
          </div>
        )}

        <div className="max-h-[380px] overflow-y-auto -mx-2 px-2" data-testid="statement-client-list">
          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Recherche en cours…
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm italic">
              {query ? 'Aucun client trouvé pour cette recherche.' : 'Aucun client disponible.'}
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {items.map((it) => {
                const hasDebt = it.totalDue > 0;
                return (
                  <li key={it.id}>
                    <button
                      onClick={() => handlePick(it)}
                      className="w-full flex items-center gap-3 py-2.5 px-2 hover:bg-emerald-50/60 rounded text-left transition-colors group"
                      data-testid={`statement-pick-${it.id}`}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                        hasDebt ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {it.isCompany ? <Building2 className="w-4 h-4" /> : <User className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate group-hover:text-emerald-700">
                          {it.displayName}
                        </div>
                        <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-2">
                          {hasDebt ? (
                            <>
                              <span className="font-mono font-semibold text-gray-800">{formatExact(it.totalDue)} FCFA</span>
                              <span className="text-gray-400">·</span>
                              <span>{it.invoiceCount} facture{it.invoiceCount > 1 ? 's' : ''} impayée{it.invoiceCount > 1 ? 's' : ''}</span>
                            </>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-emerald-600">
                              <CheckCircle className="w-3 h-3" /> Compte à jour
                            </span>
                          )}
                        </div>
                      </div>
                      <FileBarChart className="w-4 h-4 text-gray-300 group-hover:text-emerald-600 shrink-0" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StatementGeneratorModal;
